import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import './ShipmentsPage.css';
import MonthCalendar from '../../components/shipments/MonthCalendar';
import DayDetailsDrawer from '../../components/shipments/DayDetailsDrawer';
import ShipmentsGraph from '../../components/shipments/ShipmentsGraph';
import LoadingSpinner from '../../components/LoadingSpinner';
import { fetchMonthSummary, warmBackend } from '../../services/shipmentsApi';
import useTvKiosk from '../../hooks/useTvKiosk';
import { normalizeRoleToken } from '../../roles';
import {
  getOsloToday,
  getZonedDateParts,
  zonedStartOfDay,
  getTimeZone
} from '../../components/shipments/timezone';
import { formatDDMMYYYY } from '../../utils/dates';

const monthFormatter = new Intl.DateTimeFormat('en-GB', {
  month: 'long',
  year: 'numeric',
  timeZone: getTimeZone()
});

const timeFormatter = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  timeZone: getTimeZone()
});

function collectRoles(user) {
  const sources = [
    user?.publicMetadata?.roles,
    user?.privateMetadata?.roles,
    user?.unsafeMetadata?.roles,
    user?.roles,
    user?.publicMetadata?.role,
    user?.privateMetadata?.role,
    user?.unsafeMetadata?.role,
    user?.role
  ];
  const set = new Set();
  sources.forEach(source => {
    if (Array.isArray(source)) {
      source.forEach(value => {
        const normalized = normalizeRoleToken(String(value));
        if (normalized) set.add(normalized);
      });
      return;
    }
    if (typeof source === 'string') {
      const normalized = normalizeRoleToken(source);
      if (normalized) set.add(normalized);
    }
  });
  return Array.from(set);
}

function useCountUp(target) {
  const [displayValue, setDisplayValue] = useState(0);
  useEffect(() => {
    const next = Number.isFinite(target) ? target : 0;
    const start = displayValue;
    const diff = next - start;
    if (diff === 0) return undefined;
    const duration = 600;
    let frame = null;
    let startTime = null;
    const step = timestamp => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min(1, (timestamp - startTime) / duration);
      const value = Math.round(start + diff * progress);
      setDisplayValue(value);
      if (progress < 1) {
        frame = requestAnimationFrame(step);
      }
    };
    frame = requestAnimationFrame(step);
    return () => {
      if (frame) cancelAnimationFrame(frame);
    };
  }, [target, displayValue]);
  useEffect(() => {
    if (!Number.isFinite(target)) {
      setDisplayValue(0);
    }
  }, [target]);
  return displayValue;
}

export default function ShipmentsPage() {
  const { user } = useUser();
  const kiosk = useTvKiosk();
  const userId = user?.id || null;
  const roles = useMemo(() => collectRoles(user), [user]);
  const isServiceUser = useMemo(
    () => roles.some(role => role.startsWith('service')),
    [roles]
  );
  const isAdmin = useMemo(
    () => roles.some(role => role === 'admin'),
    [roles]
  );
  const todayParts = useMemo(() => getZonedDateParts(getOsloToday()), []);
  const [monthState, setMonthState] = useState({ year: todayParts.year, month: todayParts.month });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [view, setView] = useState('calendar');
  const [selectedDate, setSelectedDate] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const hasWarmedRef = useRef(false);
  const selectedDateRef = useRef(null);

  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  const { year, month } = monthState;

  const goToOffset = useCallback(offset => {
    setMonthState(prev => {
      const total = (prev.year * 12 + (prev.month - 1)) + offset;
      const nextYear = Math.floor(total / 12);
      const nextMonth = (total % 12) + 1;
      return { year: nextYear, month: nextMonth };
    });
  }, []);

  const handlePrevMonth = useCallback(() => goToOffset(-1), [goToOffset]);
  const handleNextMonth = useCallback(() => goToOffset(1), [goToOffset]);

  useEffect(() => {
    const handler = event => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePrevMonth();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNextMonth();
      } else if (event.key === 'f' || event.key === 'F') {
        event.preventDefault();
        kiosk.toggleFullscreen();
      } else if (event.key === 'Escape' && document.fullscreenElement) {
        document.exitFullscreen?.();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [kiosk, handlePrevMonth, handleNextMonth]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      setLoading(true);
      setStatus({ tone: 'info', message: 'Fetching shipments…' });

      if (!hasWarmedRef.current) {
        setStatus({ tone: 'info', message: 'Preparing shipments service…' });
        try {
          await warmBackend();
        } catch (err) {
          console.warn('Failed to warm backend before shipments load', err);
        }
        hasWarmedRef.current = true;
        if (cancelled) {
          return;
        }
      }

      setStatus({ tone: 'info', message: 'Fetching shipments…' });

      try {
        const response = await fetchMonthSummary({
          year,
          month,
          signal: controller.signal,
          onRetry: ({ attempt, total }) => {
            if (cancelled) return;
            setStatus({ tone: 'info', message: `Couldn't reach server, retrying… (${attempt}/${total})` });
          },
        });
        if (cancelled) return;
        if (response?.ok === false) {
          if (response?.status === 403 || response?.error === 'forbidden') {
            if (!isServiceUser && !isAdmin) {
              setStatus({ tone: 'error', message: 'No access to shipments analytics.' });
            } else {
              console.error('Unexpected 403 for authorized user when loading shipments month summary', {
                userId,
                roles,
              });
              setStatus({ tone: 'error', message: 'Failed to load shipments (auth). Please refresh or contact support.' });
            }
            setSummary(null);
            setSelectedDate(null);
            return;
          }
          throw new Error(response?.error || 'Failed to load shipments');
        }
        setSummary(response);
        setLastRefresh(Date.now());
        setStatus(null);
        const currentSelected = selectedDateRef.current;
        if (currentSelected && !response?.days?.some(day => day?.date === currentSelected)) {
          setSelectedDate(null);
        }
      } catch (err) {
        if (cancelled) return;
        if (err?.name === 'AbortError') return;
        const friendly = err instanceof TypeError
          ? "Couldn't reach server. Please try again."
          : err?.message || 'Failed to load shipments';
        setStatus({ tone: 'error', message: friendly });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [year, month, kiosk.refreshToken, isServiceUser, isAdmin, roles, userId]);

  const calendarDays = useMemo(() => {
    if (!Array.isArray(summary?.days)) return [];
    return summary.days
      .filter(day => typeof day?.date === 'string')
      .map(day => ({
        date: day.date,
        count: Number.isFinite(day.count) ? day.count : 0,
      }));
  }, [summary]);

  const totalMonthCount = useMemo(
    () => calendarDays.reduce((acc, day) => acc + (Number.isFinite(day.count) ? day.count : 0), 0),
    [calendarDays]
  );

  const productNumbers = useMemo(
    () => (Array.isArray(summary?.productNumbers) ? summary.productNumbers : []),
    [summary]
  );

  const displayCount = useCountUp(totalMonthCount);

  const monthLabel = useMemo(() => {
    const anchor = zonedStartOfDay(year, month, 1);
    return monthFormatter.format(anchor);
  }, [year, month]);

  const refreshedLabel = useMemo(() => timeFormatter.format(new Date(lastRefresh)), [lastRefresh]);

  const handleSelectDate = useCallback(iso => {
    if (!iso) return;
    setSelectedDate(prev => (prev === iso ? null : iso));
  }, []);

  const closeDrawer = useCallback(() => setSelectedDate(null), []);

  const drawerDisplayDate = useMemo(
    () => (selectedDate ? formatDDMMYYYY(`${selectedDate}T00:00:00`) : null),
    [selectedDate]
  );

  return (
    <div className={`shipments-page ${kiosk.hideChrome ? 'shipments-page--tv' : ''}`}>
      <header>
        <div>
          <div className="shipments-brand">
            <Link to="/" title="Back to main page" aria-label="Back to main page" style={{ display: 'inline-flex', lineHeight: 0 }}>
              <img src="/north-invent-logo.svg" alt="North Invent" />
            </Link>
            <div className="shipments-brand-copy">
              <span className="shipments-kicker">Service logistics</span>
          <h1>{view === 'graph' ? 'Production Trend' : `New Shipments — ${monthLabel}`}</h1>
            </div>
          </div>
          {!kiosk.hideChrome && (
            <div className="toolbar">
              {view === 'calendar' && (
                <>
                  <div className="month-switcher">
                    <button type="button" onClick={handlePrevMonth} aria-label="Previous month">◀</button>
                    <span>{monthLabel}</span>
                    <button type="button" onClick={handleNextMonth} aria-label="Next month">▶</button>
                  </div>
                  <button type="button" onClick={() => setMonthState({ year: todayParts.year, month: todayParts.month })}>
                    Jump to current month
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setView(v => v === 'calendar' ? 'graph' : 'calendar')}
                style={view === 'graph' ? { borderColor: 'rgba(70,187,137,0.5)', color: '#46BB89', background: 'rgba(70,187,137,0.1)' } : {}}
              >
                {view === 'calendar' ? '📈 Trend view' : '📅 Calendar view'}
              </button>
            </div>
          )}
        </div>
        <div className="counter">Shipped this month: {displayCount}</div>
      </header>
      {!kiosk.hideChrome && (
        <div className="refresh-indicator">Last refreshed {refreshedLabel}</div>
      )}
      {status && (
        <div className={`status-banner status-banner--${status.tone}`}>{status.message}</div>
      )}
      {view === 'graph' ? (
        <div className="calendar-container" style={{ alignItems: 'stretch' }}>
          <ShipmentsGraph />
        </div>
      ) : (
        <>
          <div className="calendar-container">
            <div className="calendar-wrapper">
              {loading && !summary ? (
                <div className="calendar-loading">
                  <LoadingSpinner size={64} />
                </div>
              ) : (
                <MonthCalendar
                  year={year}
                  month={month}
                  days={calendarDays}
                  selectedDate={selectedDate}
                  onSelectDate={handleSelectDate}
                />
              )}
            </div>
            <aside className="shipments-legend">
              <h2>Product Numbers</h2>
              {productNumbers.length > 0 ? (
                <ul>
                  {productNumbers.map(productNumber => (
                    <li key={productNumber}>{productNumber}</li>
                  ))}
                </ul>
              ) : (
                <p>No product numbers recorded this month.</p>
              )}
            </aside>
          </div>
          <DayDetailsDrawer
            isoDate={selectedDate}
            displayDate={drawerDisplayDate}
            onClose={closeDrawer}
            isServiceUser={isServiceUser || isAdmin}
          />
        </>
      )}
    </div>
  );
}
